/**
 * ResumenTab — Patient Snapshot Dashboard (Epic Cosmos style, 2 columnas)
 *
 * Layout:
 *   [ HERO: Briefing IA + Quick Actions ]
 *   [ KPI STRIP ]
 *   [ Col izq (40%) — contexto del paciente ]
 *   [ Col der (60%) — info temporal/accionable ]
 */
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Skeleton, Button, Tooltip,
  TextField, IconButton, Dialog, Slide,
} from '@mui/material';
import { inputStyles } from '../common';
import {
  AutoAwesome as AutoAwesomeIcon,
  Medication as MedicationIcon,
  Healing as HealingIcon,
  MonitorHeart as VitalsIcon,
  Science as ScienceIcon,
  CalendarMonth as CalendarIcon,
  Cake as CakeIcon,
  History as HistoryIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Warning as WarningIcon,
  FamilyRestroom as FamilyIcon,
  Print as PrintIcon,
  Add as AddIcon,
  Event as EventIcon,
  ArrowForward as ArrowForwardIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  ChevronRight as ChevronRightIcon,
  Biotech as BiotechIcon,
  Remove as RemoveIcon,
  PushPin as PushPinIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';
import { extraerProblemasActivos, extraerAlergias } from './AlertasBanner';

const API_URL = '/api';

const SlideUp = React.forwardRef(function SlideUp(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Paleta semántica
const C = {
  indigo: { main: '#4f46e5', bg: '#eef2ff', text: '#3730a3', light: '#c7d2fe' },
  rojo:   { main: '#ef4444', bg: '#fee2e2', text: '#991b1b', light: '#fecaca' },
  morado: { main: '#8b5cf6', bg: '#ede9fe', text: '#5b21b6', light: '#ddd6fe' },
  ambar:  { main: '#f59e0b', bg: '#fef3c7', text: '#92400e', light: '#fde68a' },
  verde:  { main: '#10b981', bg: '#d1fae5', text: '#065f46', light: '#a7f3d0' },
  azul:   { main: '#0ea5e9', bg: '#e0f2fe', text: '#075985', light: '#bae6fd' },
  rosa:   { main: '#ec4899', bg: '#fce7f3', text: '#9f1239', light: '#fbcfe8' },
  slate:  { main: '#64748b', bg: '#f1f5f9', text: '#334155', light: '#e2e8f0' },
};

export default function ResumenTab({ paciente, expediente, userId, onNuevaConsulta, onImprimir, onIrAntecedentes }) {
  const [consultas, setConsultas] = useState([]);
  const [recetas, setRecetas] = useState([]);
  const [estudios, setEstudios] = useState([]);
  const [proximasCitas, setProximasCitas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [notaPinneada, setNotaPinneada] = useState(expediente?.nota_pinneada || '');
  const [notaActualizada, setNotaActualizada] = useState(expediente?.nota_pinneada_actualizada || null);

  useEffect(() => {
    setNotaPinneada(expediente?.nota_pinneada || '');
    setNotaActualizada(expediente?.nota_pinneada_actualizada || null);
  }, [expediente?.id, expediente?.nota_pinneada, expediente?.nota_pinneada_actualizada]);

  useEffect(() => {
    if (!expediente?.id) return;
    let cancelado = false;
    (async () => {
      setCargando(true);
      try {
        const headers = { 'X-User-Id': userId };
        const [rExp, rCitas] = await Promise.all([
          fetch(`${API_URL}/emr/expedientes/${expediente.id}/completo`, { headers }),
          paciente?.id
            ? fetch(`${API_URL}/emr/citas?paciente_id=${paciente.id}&desde_hoy=true&limit=10`, { headers })
                .catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelado) return;
        if (rExp.ok) {
          const data = await rExp.json();
          setConsultas((data.consultas || []).sort(
            (a, b) => new Date(b.fecha_consulta) - new Date(a.fecha_consulta)
          ));
          setRecetas(data.recetas || []);
          setEstudios(data.estudios || []);
        }
        if (rCitas?.ok) {
          const data = await rCitas.json();
          const futuras = (data.citas || data || [])
            .filter(c => new Date(c.fecha_hora) >= new Date())
            .sort((a, b) => new Date(a.fecha_hora) - new Date(b.fecha_hora));
          setProximasCitas(futuras);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();
    return () => { cancelado = true; };
  }, [expediente?.id, paciente?.id, userId]);

  const alergias = extraerAlergias(expediente);
  const problemas = extraerProblemasActivos(expediente);
  const meds = extraerMedicamentosActivos(recetas);
  const estudiosPendientes = estudios.filter(e => !e.fecha_resultado);
  const heredoFamiliares = extraerHeredoFamiliares(expediente);
  const noPatologicos = extraerNoPatologicos(expediente);

  const edad = calcEdad(paciente?.fecha_nacimiento);
  const ultimaVisita = consultas[0];

  // Datos del gráfico con tendencia
  const datosGrafico = useMemo(() => {
    return consultas
      .filter(c => c.signos_vitales && Object.keys(c.signos_vitales).length > 0)
      .slice(0, 12)
      .reverse()
      .map(c => {
        const sv = c.signos_vitales || {};
        const f = new Date(c.fecha_consulta);
        const ta = sv.tension_arterial || sv.ta || `${sv.presion_arterial_sistolica || ''}/${sv.presion_arterial_diastolica || ''}`;
        const [sys, dia] = String(ta).split('/').map(v => parseInt(v));
        return {
          fecha: `${f.getDate()}/${f.getMonth() + 1}`,
          TA_sys: sys || null,
          TA_dia: dia || null,
          FC: parseInt(sv.frecuencia_cardiaca || sv.fc) || null,
          Temp: parseFloat(sv.temperatura || sv.temp) || null,
        };
      });
  }, [consultas]);

  const tendencia = calcularTendencia(datosGrafico);

  // ============ ANCHORS para scroll-spy ============
  // Lista declarativa de secciones del Resumen. Cada una se renderiza condicionalmente
  // (solo aparece si hay contenido). El sidebar usa esta lista para los anchors.
  const _alergiasParaAnchors = useMemo(() => extraerAlergias(expediente), [expediente]);
  const _heredoLenAnchors = useMemo(() => {
    const h = expediente?.antecedentes_heredo_familiares;
    if (!h) return 0;
    if (Array.isArray(h)) return h.length;
    if (typeof h === 'string') return h.trim() ? 1 : 0;
    if (typeof h === 'object') return Object.keys(h).length;
    return 0;
  }, [expediente]);
  const _noPatologicosLenAnchors = useMemo(() => {
    const n = expediente?.antecedentes_personales_no_patologicos;
    if (!n) return 0;
    if (Array.isArray(n)) return n.length;
    if (typeof n === 'string') return n.trim() ? 1 : 0;
    if (typeof n === 'object') return Object.keys(n).length;
    return 0;
  }, [expediente]);

  const anchorsConfig = useMemo(() => [
    { id: 'briefing', label: 'Briefing', icon: <AutoAwesomeIcon sx={{ fontSize: 14 }} />, visible: true },
    { id: 'kpis', label: 'KPIs', icon: <HistoryIcon sx={{ fontSize: 14 }} />, visible: true },
    { id: 'alergias', label: 'Alergias', icon: <WarningIcon sx={{ fontSize: 14 }} />, visible: _alergiasParaAnchors.length > 0 },
    { id: 'problemas', label: 'Problemas', icon: <HealingIcon sx={{ fontSize: 14 }} />, visible: true },
    { id: 'meds', label: 'Medicación', icon: <MedicationIcon sx={{ fontSize: 14 }} />, visible: true },
    { id: 'heredo', label: 'Heredo-familiares', icon: <FamilyIcon sx={{ fontSize: 14 }} />, visible: _heredoLenAnchors > 0 },
    { id: 'estilo', label: 'Estilo de vida', icon: <CheckCircleIcon sx={{ fontSize: 14 }} />, visible: _noPatologicosLenAnchors > 0 },
    { id: 'ap', label: 'Última A/P', icon: <AssignmentIcon sx={{ fontSize: 14 }} />, visible: true },
    { id: 'labs', label: 'Laboratorios', icon: <BiotechIcon sx={{ fontSize: 14 }} />, visible: true },
    { id: 'timeline', label: 'Timeline', icon: <ScheduleIcon sx={{ fontSize: 14 }} />, visible: consultas.length > 0 },
    { id: 'vitales', label: 'Signos vitales', icon: <VitalsIcon sx={{ fontSize: 14 }} />, visible: true },
    { id: 'proximas', label: 'Próximas acciones', icon: <EventIcon sx={{ fontSize: 14 }} />, visible: true },
  ].filter(a => a.visible), [_alergiasParaAnchors.length, _heredoLenAnchors, _noPatologicosLenAnchors, consultas.length]);

  const [activeAnchor, setActiveAnchor] = useState('briefing');

  useEffect(() => {
    const ids = anchorsConfig.map(a => a.id);
    if (ids.length === 0 || cargando) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibles = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visibles.length > 0) {
          setActiveAnchor(visibles[0].target.id);
        }
      },
      { rootMargin: '-80px 0px -50% 0px', threshold: 0 }
    );

    const elements = ids.map(id => document.getElementById(id)).filter(Boolean);
    elements.forEach(el => observer.observe(el));
    return () => elements.forEach(el => observer.unobserve(el));
  }, [anchorsConfig, cargando]);

  const handleAnchorClick = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveAnchor(id);
    }
  }, []);

  if (cargando) {
    return (
      <Box>
        <Skeleton variant="rounded" height={120} sx={{ borderRadius: 3, mb: 2 }} />
        <Grid container spacing={2}>
          {[...Array(5)].map((_, i) => (
            <Grid key={i} item xs={6} sm={4} md={2.4}>
              <Skeleton variant="rounded" height={70} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  const briefing = construirBriefing(paciente, consultas, tendencia, problemas);

  const guardarNotaPinneada = async (texto) => {
    if (!expediente?.id) return;
    const res = await fetch(`${API_URL}/emr/expedientes/${expediente.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'X-User-Id': userId },
      body: JSON.stringify({ nota_pinneada: texto || null }),
    });
    if (!res.ok) throw new Error('No se pudo guardar la nota');
    const data = await res.json();
    setNotaPinneada(data.nota_pinneada || '');
    setNotaActualizada(data.nota_pinneada_actualizada || null);
  };

  // Heurística: ¿el expediente tiene antecedentes capturados?
  const tieneAntecedentes = (() => {
    if (!expediente) return true;
    const hasContent = (obj) => {
      if (!obj || typeof obj !== 'object') return false;
      const keys = Object.keys(obj);
      if (keys.length === 0) return false;
      return keys.some(k => {
        const v = obj[k];
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'string') return v.trim().length > 0;
        if (typeof v === 'object' && v !== null) return Object.keys(v).length > 0;
        if (typeof v === 'number') return true;
        if (typeof v === 'boolean') return v === true;
        return false;
      });
    };
    return hasContent(expediente.antecedentes_heredo_familiares) ||
           hasContent(expediente.antecedentes_personales_patologicos) ||
           hasContent(expediente.antecedentes_personales_no_patologicos);
  })();

  return (
    <Box>
      {/* ============ BANNER: faltan antecedentes (NOM-024 / Resolución 1995) ============ */}
      {!tieneAntecedentes && (
        <Box sx={{
          mb: 2, p: 2, borderRadius: 2,
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '1px solid #f59e0b',
          display: 'flex', alignItems: 'center', gap: 2,
          boxShadow: '0 2px 8px rgba(245,158,11,0.15)',
        }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            background: '#92400e', color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <DescriptionIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 13.5, color: '#78350f', mb: 0.3 }}>
              Falta capturar antecedentes
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#92400e' }}>
              Antes de iniciar consultas hay que registrar antecedentes (NOM-024 / Resolución 1995). Captura al menos uno de los tres grupos básicos.
            </Typography>
          </Box>
          {onIrAntecedentes && (
            <Button
              variant="contained"
              size="small"
              onClick={onIrAntecedentes}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 12,
                background: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)',
                boxShadow: '0 4px 10px rgba(180,83,9,0.3)',
                '&:hover': { background: 'linear-gradient(135deg, #92400e 0%, #78350f 100%)' },
                whiteSpace: 'nowrap',
              }}
            >
              Capturar ahora
            </Button>
          )}
        </Box>
      )}

      {/* ============ NOTA PINNEADA — banderas críticas siempre visibles ============ */}
      <NotaPinneada
        nota={notaPinneada}
        actualizada={notaActualizada}
        onGuardar={guardarNotaPinneada}
      />

      {/* ============ FLEX LAYOUT: Sidebar Anchors + Main Content ============ */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

        {/* === ANCHOR SIDEBAR — sticky, hidden on xs/sm === */}
        <Box sx={{
          width: 188,
          flexShrink: 0,
          display: { xs: 'none', md: 'block' },
          position: 'sticky',
          top: 88,
          alignSelf: 'flex-start',
          maxHeight: 'calc(100vh - 96px)',
          overflowY: 'auto',
        }}>
          <Box sx={{
            p: 1, borderRadius: 2,
            bgcolor: '#fff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
          }}>
            <Typography sx={{
              fontSize: 9.5, fontWeight: 800, color: '#94a3b8',
              textTransform: 'uppercase', letterSpacing: 0.8,
              px: 1, mb: 0.6,
            }}>
              En esta vista
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
              {anchorsConfig.map((a) => {
                const sel = activeAnchor === a.id;
                return (
                  <Box
                    key={a.id}
                    onClick={() => handleAnchorClick(a.id)}
                    sx={{
                      position: 'relative',
                      display: 'flex', alignItems: 'center', gap: 0.7,
                      px: 1, py: 0.6,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      bgcolor: sel ? 'rgba(79, 70, 229, 0.10)' : 'transparent',
                      transition: 'all 0.15s',
                      '&:hover': {
                        bgcolor: sel ? 'rgba(79, 70, 229, 0.13)' : 'rgba(79, 70, 229, 0.05)',
                      },
                    }}
                  >
                    {sel && (
                      <Box sx={{
                        position: 'absolute',
                        left: 0, top: 4, bottom: 4,
                        width: 3, borderRadius: 2,
                        background: 'linear-gradient(180deg, #fbbf24 0%, #f59e0b 100%)',
                        boxShadow: '0 0 6px rgba(251, 191, 36, 0.45)',
                      }} />
                    )}
                    <Box sx={{
                      color: sel ? '#4f46e5' : '#94a3b8',
                      display: 'flex', alignItems: 'center',
                      ml: sel ? 0.4 : 0,
                      transition: 'margin 0.15s',
                    }}>
                      {a.icon}
                    </Box>
                    <Typography sx={{
                      fontSize: 11.5,
                      fontWeight: sel ? 700 : 500,
                      color: sel ? '#1e293b' : '#64748b',
                      lineHeight: 1.2,
                      flex: 1,
                    }}>
                      {a.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>

        {/* === MAIN CONTENT === */}
        <Box sx={{ flex: 1, minWidth: 0 }}>

      {/* ============ HERO: Briefing IA + Quick Actions ============ */}
      <Paper id="briefing" sx={{
        scrollMarginTop: 88,
        p: 2.5, mb: 2, borderRadius: 3,
        background: 'linear-gradient(135deg, #eef2ff 0%, #ede9fe 60%, #fef3c7 100%)',
        border: '1px solid #c7d2fe',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 2,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, boxShadow: '0 4px 14px rgba(99,102,241,0.35)',
          }}>
            <AutoAwesomeIcon sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography sx={{
              fontSize: 10, fontWeight: 800, color: '#4338ca',
              letterSpacing: 0.8, textTransform: 'uppercase', mb: 0.4,
            }}>
              Briefing NARA · Resumen ejecutivo
            </Typography>
            <Typography sx={{ fontSize: 14.5, color: '#1e293b', lineHeight: 1.6, fontWeight: 500 }}>
              {briefing}
            </Typography>
          </Box>
        </Box>

        {/* Quick Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {onNuevaConsulta && (
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={onNuevaConsulta}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 12,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                boxShadow: '0 3px 10px rgba(99,102,241,0.35)',
                '&:hover': { background: 'linear-gradient(135deg, #4f46e5, #7c3aed)' },
              }}
            >
              Nueva consulta
            </Button>
          )}
          {onImprimir && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              onClick={onImprimir}
              sx={{
                textTransform: 'none', fontWeight: 600, fontSize: 12,
                borderRadius: 2, borderColor: '#a5b4fc', color: '#4338ca',
                background: 'rgba(255,255,255,0.7)',
                '&:hover': { background: '#fff', borderColor: '#6366f1' },
              }}
            >
              Imprimir resumen
            </Button>
          )}
        </Box>
      </Paper>

      {/* ============ KPI STRIP ============ */}
      <Grid id="kpis" container spacing={1.5} sx={{ mb: 2, scrollMarginTop: 88 }} alignItems="stretch">
        <Grid item xs={6} sm={4} md={2.4}>
          <KPI icono={<CakeIcon />} valor={edad} sufijo="años" label={paciente?.sexo || ''} color={C.slate} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <KPI icono={<HistoryIcon />} valor={consultas.length} label="consultas" color={C.indigo} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <KPI
            icono={<CalendarIcon />}
            valor={ultimaVisita ? diasDesde(ultimaVisita.fecha_consulta) : '—'}
            sufijo={ultimaVisita ? 'd' : ''}
            label="última visita"
            color={C.azul}
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <KPI icono={<MedicationIcon />} valor={meds.length} label="meds activos" color={C.morado} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <KPI icono={<PendingIcon />} valor={estudiosPendientes.length} label="estudios pendientes" color={C.ambar} />
        </Grid>
      </Grid>

      {/* ============ LAYOUT 2 COLUMNAS ============ */}
      <Grid container spacing={2}>

        {/* ===== COLUMNA IZQUIERDA — 40% — contexto del paciente ===== */}
        <Grid item xs={12} md={5}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Alergias críticas (solo si las hay) */}
            {alergias.length > 0 && (
              <Paper id="alergias" sx={{
                scrollMarginTop: 88,
                p: 1.8, borderRadius: 3,
                background: 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)',
                border: '2px solid #ef4444',
                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.12)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Box sx={{
                    width: 28, height: 28, borderRadius: 1.5,
                    background: '#ef4444',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <WarningIcon sx={{ fontSize: 18, color: '#fff' }} />
                  </Box>
                  <Typography sx={{
                    fontWeight: 800, fontSize: 12, color: '#991b1b',
                    textTransform: 'uppercase', letterSpacing: 0.5,
                  }}>
                    🚨 Alergias críticas ({alergias.length})
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {alergias.map((a, i) => (
                    <Chip
                      key={i} label={a} size="small"
                      sx={{
                        background: '#fff',
                        color: '#991b1b',
                        fontWeight: 700, fontSize: 12,
                        border: '1.5px solid #fca5a5',
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}

            {/* Problemas crónicos */}
            <Box id="problemas" sx={{ scrollMarginTop: 88 }}>
            <SeccionLateral
              titulo="Problemas crónicos"
              icono={<HealingIcon />}
              color={C.rojo}
              cantidad={problemas.length}
              vacio="Sin problemas crónicos registrados"
            >
              {problemas.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  {problemas.map((p, i) => (
                    <ItemLateral key={i} texto={p} color={C.rojo} />
                  ))}
                </Box>
              )}
            </SeccionLateral>
            </Box>

            {/* Medicación activa */}
            <Box id="meds" sx={{ scrollMarginTop: 88 }}>
            <SeccionLateral
              titulo="Medicación activa"
              icono={<MedicationIcon />}
              color={C.morado}
              cantidad={meds.length}
              vacio="Sin medicación activa"
            >
              {meds.slice(0, 6).map((m, i) => (
                <Box key={i} sx={{ py: 0.6, borderBottom: i < Math.min(meds.length, 6) - 1 ? '1px solid #f1f5f9' : 'none' }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.morado.text, lineHeight: 1.3 }}>
                    {m.nombre}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.1 }}>
                    {[
                      m.dosis_mg && `${m.dosis_mg}mg`,
                      m.dosis,
                      m.via && `vía ${m.via}`,
                      m.frecuencia_horas && `c/${m.frecuencia_horas}h`,
                      m.frecuencia,
                      m.duracion_dias && `× ${m.duracion_dias}d`,
                      m.duracion,
                    ].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                </Box>
              ))}
              {meds.length > 6 && (
                <Typography sx={{ fontSize: 11, color: C.morado.text, mt: 0.5, fontStyle: 'italic' }}>
                  + {meds.length - 6} más
                </Typography>
              )}
            </SeccionLateral>
            </Box>

            {/* Antecedentes heredo-familiares */}
            {heredoFamiliares.length > 0 && (
              <Box id="heredo" sx={{ scrollMarginTop: 88 }}>
              <SeccionLateral
                titulo="Heredo-familiares"
                icono={<FamilyIcon />}
                color={C.rosa}
                cantidad={heredoFamiliares.length}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                  {heredoFamiliares.map((h, i) => (
                    <Chip
                      key={i} label={h} size="small"
                      sx={{
                        background: C.rosa.bg, color: C.rosa.text,
                        fontWeight: 600, fontSize: 11, height: 22,
                      }}
                    />
                  ))}
                </Box>
              </SeccionLateral>
              </Box>
            )}

            {/* Antecedentes no patológicos */}
            {noPatologicos.length > 0 && (
              <Box id="estilo" sx={{ scrollMarginTop: 88 }}>
              <SeccionLateral
                titulo="Estilo de vida"
                icono={<AutoAwesomeIcon />}
                color={C.verde}
                cantidad={noPatologicos.length}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  {noPatologicos.map((n, i) => (
                    <ItemLateral key={i} texto={n} color={C.verde} />
                  ))}
                </Box>
              </SeccionLateral>
              </Box>
            )}

          </Box>
        </Grid>

        {/* ===== COLUMNA DERECHA — 60% — info temporal/accionable ===== */}
        <Grid item xs={12} md={7}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* Último A/P — assessment + plan de la consulta más reciente */}
            <Box id="ap" sx={{ scrollMarginTop: 88 }}>
              <UltimoAP consulta={ultimaVisita} />
            </Box>

            {/* Últimos resultados de laboratorio con tendencia */}
            <Box id="labs" sx={{ scrollMarginTop: 88 }}>
              <UltimosLabs consultas={consultas} />
            </Box>

            {/* Timeline horizontal de últimas consultas */}
            <Paper id="timeline" sx={{ scrollMarginTop: 88, p: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <SeccionHeader
                icono={<ScheduleIcon />}
                titulo="Línea de tiempo clínica"
                subtitulo={`Últimas ${Math.min(consultas.length, 6)} consultas`}
                color={C.indigo}
              />
              {consultas.length === 0 ? (
                <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                  Sin consultas registradas todavía
                </Typography>
              ) : (
                <TimelineHorizontal consultas={consultas.slice(0, 6)} />
              )}
            </Paper>

            {/* Tendencia signos vitales */}
            <Paper id="vitales" sx={{ scrollMarginTop: 88, p: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <SeccionHeader
                  icono={<VitalsIcon />}
                  titulo="Tendencia de signos vitales"
                  subtitulo={`${datosGrafico.length} mediciones`}
                  color={C.azul}
                  noMargin
                />
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {tendencia.TA && <BadgeTendencia label="TA" direccion={tendencia.TA} />}
                  {tendencia.FC && <BadgeTendencia label="FC" direccion={tendencia.FC} />}
                </Box>
              </Box>

              {datosGrafico.length < 2 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                  <VitalsIcon sx={{ fontSize: 36, color: C.slate.light, mb: 1 }} />
                  <Typography sx={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
                    Se necesitan al menos 2 consultas con signos vitales
                  </Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={datosGrafico} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                    <XAxis dataKey="fecha" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                    <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#e2e8f0' }} tickLine={false} />
                    <RTooltip contentStyle={{ borderRadius: 8, border: '1px solid #e2e8f0', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', fontSize: 11 }} />
                    <Legend iconType="circle" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    <Line type="monotone" dataKey="TA_sys" stroke={C.rojo.main} name="TA sis" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="TA_dia" stroke={C.ambar.main} name="TA dia" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="FC" stroke={C.indigo.main} name="FC" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 5 }} />
                    <Line type="monotone" dataKey="Temp" stroke={C.verde.main} name="Temp" strokeWidth={2} dot={{ r: 2.5 }} activeDot={{ r: 5 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </Paper>

            {/* Próximas acciones */}
            <Paper id="proximas" sx={{ scrollMarginTop: 88, p: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
              <SeccionHeader
                icono={<EventIcon />}
                titulo="Próximas acciones"
                subtitulo="Citas + estudios pendientes"
                color={C.verde}
              />
              <ProximasAcciones
                proximasCitas={proximasCitas}
                estudiosPendientes={estudiosPendientes}
              />
            </Paper>

          </Box>
        </Grid>
      </Grid>

        </Box>{/* /Main Content */}
      </Box>{/* /Flex Layout */}
    </Box>
  );
}

// ============================================================================
// SUBCOMPONENTES
// ============================================================================

function KPI({ icono, valor, sufijo, label, color }) {
  return (
    <Paper sx={{
      p: 1.3, borderRadius: 2.5, border: '1px solid #e2e8f0',
      height: 70, display: 'flex', alignItems: 'center', gap: 1,
      background: '#fff', overflow: 'hidden',
      transition: 'all 0.15s',
      '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.06)', borderColor: color.light },
    }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: 1.5,
        background: color.bg, color: color.main,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {React.cloneElement(icono, { sx: { fontSize: 19 } })}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
          <Typography sx={{ fontSize: 21, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
            {valor}
          </Typography>
          {sufijo && (
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>
              {sufijo}
            </Typography>
          )}
        </Box>
        <Typography sx={{
          fontSize: 9.5, fontWeight: 700, color: color.text,
          textTransform: 'uppercase', letterSpacing: 0.5, mt: 0.2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}

function SeccionHeader({ icono, titulo, subtitulo, color, noMargin = false }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: noMargin ? 0 : 1.2 }}>
      <Box sx={{
        width: 30, height: 30, borderRadius: 1.5,
        background: `linear-gradient(135deg, ${color.main}, ${color.text})`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: `0 3px 8px ${color.main}33`,
      }}>
        {React.cloneElement(icono, { sx: { fontSize: 16, color: '#fff' } })}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 800, color: '#1e293b', fontSize: 13, lineHeight: 1.2 }}>
          {titulo}
        </Typography>
        {subtitulo && (
          <Typography sx={{ fontSize: 10.5, color: '#64748b' }}>
            {subtitulo}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function SeccionLateral({ titulo, icono, color, cantidad, vacio, children }) {
  const esVacio = cantidad === 0;
  return (
    <Paper sx={{
      borderRadius: 3,
      border: `1px solid ${color.light}`,
      background: `linear-gradient(180deg, ${color.bg}30 0%, #fff 50%)`,
      overflow: 'hidden',
    }}>
      <Box sx={{ height: 3, background: color.main }} />
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1 }}>
          <Box sx={{
            width: 24, height: 24, borderRadius: 1.2,
            background: color.bg, color: color.main,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            {React.cloneElement(icono, { sx: { fontSize: 14 } })}
          </Box>
          <Typography sx={{
            fontWeight: 800, color: '#1e293b', fontSize: 12,
            textTransform: 'uppercase', letterSpacing: 0.4, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {titulo}
          </Typography>
          <Chip
            label={cantidad} size="small"
            sx={{
              background: color.main, color: '#fff',
              fontWeight: 800, height: 20, minWidth: 24, fontSize: 11,
            }}
          />
        </Box>
        <Box>
          {esVacio ? (
            <Typography sx={{
              fontSize: 11.5, color: '#94a3b8', fontStyle: 'italic',
              textAlign: 'center', py: 1,
            }}>
              {vacio || 'Sin información'}
            </Typography>
          ) : (
            children
          )}
        </Box>
      </Box>
    </Paper>
  );
}

function ItemLateral({ texto, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, py: 0.2 }}>
      <Box sx={{
        width: 5, height: 5, borderRadius: '50%',
        background: color.main, flexShrink: 0,
      }} />
      <Typography sx={{ fontSize: 12.5, color: '#334155', fontWeight: 500 }}>
        {texto}
      </Typography>
    </Box>
  );
}

function BadgeTendencia({ label, direccion }) {
  const config = {
    up:   { icon: <TrendingUpIcon sx={{ fontSize: 13 }} />,   bg: C.rojo.bg,  text: C.rojo.text,  titulo: '↑ subiendo' },
    down: { icon: <TrendingDownIcon sx={{ fontSize: 13 }} />, bg: C.verde.bg, text: C.verde.text, titulo: '↓ bajando' },
    flat: { icon: <TrendingFlatIcon sx={{ fontSize: 13 }} />, bg: C.slate.bg, text: C.slate.text, titulo: '→ estable' },
  }[direccion];
  if (!config) return null;
  return (
    <Tooltip title={`${label} ${config.titulo}`}>
      <Chip
        label={`${label} ${config.titulo}`}
        icon={config.icon}
        size="small"
        sx={{
          background: config.bg, color: config.text,
          fontWeight: 700, fontSize: 10.5, height: 22,
          '& .MuiChip-icon': { color: config.text, ml: 0.5 },
        }}
      />
    </Tooltip>
  );
}

function NotaPinneada({ nota, actualizada, onGuardar }) {
  const [abierto, setAbierto] = useState(false);
  const [borrador, setBorrador] = useState(nota || '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { setBorrador(nota || ''); }, [nota]);

  const tieneNota = Boolean(nota && nota.trim());

  const abrirEditor = () => {
    setBorrador(nota || '');
    setError('');
    setAbierto(true);
  };

  const handleGuardar = async () => {
    setGuardando(true);
    setError('');
    try {
      await onGuardar(borrador.trim());
      setAbierto(false);
    } catch (e) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleQuitar = async () => {
    setGuardando(true);
    setError('');
    try {
      await onGuardar('');
      setAbierto(false);
    } catch (e) {
      setError(e.message || 'Error al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const fechaTxt = actualizada
    ? new Date(actualizada).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })
    : null;

  return (
    <>
      {tieneNota ? (
        <Paper
          elevation={0}
          sx={{
            mb: 2, p: 1.5, borderRadius: 3,
            background: 'linear-gradient(135deg, #fef9c3 0%, #fef3c7 100%)',
            border: '1px solid #fcd34d',
            borderLeft: '4px solid #d97706',
            boxShadow: '0 2px 12px rgba(217, 119, 6, 0.18)',
            display: 'flex', alignItems: 'flex-start', gap: 1.2,
          }}
        >
          <Box sx={{
            width: 30, height: 30, borderRadius: 1.5,
            background: '#d97706',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
            transform: 'rotate(-15deg)',
            boxShadow: '0 3px 8px rgba(217, 119, 6, 0.35)',
          }}>
            <PushPinIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
              <Typography sx={{
                fontSize: 10, fontWeight: 800, color: '#92400e',
                textTransform: 'uppercase', letterSpacing: 0.6,
              }}>
                Nota fijada
              </Typography>
              {fechaTxt && (
                <Typography sx={{ fontSize: 10, color: '#a16207' }}>
                  · {fechaTxt}
                </Typography>
              )}
            </Box>
            <Typography sx={{ fontSize: 13, color: '#451a03', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {nota}
            </Typography>
          </Box>
          <Tooltip title="Editar nota fijada">
            <IconButton
              size="small"
              onClick={abrirEditor}
              sx={{
                color: '#92400e',
                '&:hover': { background: 'rgba(217, 119, 6, 0.15)' },
              }}
            >
              <EditIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Paper>
      ) : (
        <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'flex-start' }}>
          <Button
            size="small"
            variant="text"
            startIcon={<PushPinIcon sx={{ fontSize: 14, transform: 'rotate(-15deg)' }} />}
            onClick={abrirEditor}
            sx={{
              textTransform: 'none', fontSize: 11, fontWeight: 600,
              color: '#a16207',
              borderRadius: 2, px: 1.2,
              '&:hover': { background: '#fef3c7', color: '#92400e' },
            }}
          >
            Fijar nota importante
          </Button>
        </Box>
      )}

      <Dialog
        open={abierto}
        onClose={() => !guardando && setAbierto(false)}
        TransitionComponent={SlideUp}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: 'rgba(255,255,255,0.98)',
            backdropFilter: 'blur(20px)',
            boxShadow: '0 20px 60px rgba(15, 23, 42, 0.25)',
            border: '1px solid rgba(255,255,255,0.4)',
            overflow: 'hidden',
          },
        }}
      >
        {/* Header slim — single row, ~42px */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.2,
          py: 1, pl: 2, pr: 1,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          color: '#fff',
        }}>
          <PushPinIcon sx={{ fontSize: 16, color: '#fbbf24', transform: 'rotate(-15deg)' }} />
          <Typography sx={{ fontSize: 13.5, fontWeight: 700, letterSpacing: 0.2, flex: 1 }}>
            Nota fijada del paciente
          </Typography>
          <IconButton
            size="small"
            onClick={() => setAbierto(false)}
            disabled={guardando}
            sx={{
              color: 'rgba(255,255,255,0.85)', p: 0.4,
              '&:hover': { background: 'rgba(255,255,255,0.12)', color: '#fff' },
            }}
          >
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
          <TextField
            sx={inputStyles}
            fullWidth
            multiline
            minRows={3}
            maxRows={8}
            label="Nota"
            placeholder="Ej: No usar AINEs por antecedente de úlcera. Contacto: María (hija) +52..."
            value={borrador}
            onChange={(e) => setBorrador(e.target.value.slice(0, 1000))}
            disabled={guardando}
            autoFocus
            inputProps={{ maxLength: 1000 }}
            helperText={
              error
                ? `⚠ ${error}`
                : `Se mostrará siempre arriba del Resumen · ${borrador.length}/1000`
            }
            error={Boolean(error)}
          />
          {nota && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="small"
                onClick={handleQuitar}
                disabled={guardando}
                startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: 'none', color: '#dc2626', fontSize: 11, fontWeight: 600,
                  '&:hover': { background: '#fef2f2' },
                }}
              >
                Quitar nota
              </Button>
            </Box>
          )}
        </Box>

        {/* Acciones */}
        <Box sx={{ display: 'flex', gap: 1, p: 2, pt: 0 }}>
          <Button
            fullWidth
            onClick={() => setAbierto(false)}
            disabled={guardando}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: 12.5,
              borderRadius: 2, py: 1, color: '#475569',
              background: '#f1f5f9',
              '&:hover': { background: '#e2e8f0' },
            }}
          >
            Cancelar
          </Button>
          <Button
            fullWidth
            onClick={handleGuardar}
            disabled={guardando || borrador.trim() === (nota || '').trim()}
            variant="contained"
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 12.5,
              borderRadius: 2, py: 1,
              background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
              boxShadow: '0 4px 12px rgba(30, 58, 95, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1e3a5f 0%, #1e4670 100%)',
                boxShadow: '0 6px 16px rgba(30, 58, 95, 0.4)',
              },
            }}
          >
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </Box>
      </Dialog>
    </>
  );
}

function UltimoAP({ consulta }) {
  if (!consulta) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3, border: '1px dashed #cbd5e1', background: '#f8fafc' }}>
        <SeccionHeader
          icono={<AssignmentIcon />}
          titulo="Último Assessment & Plan"
          subtitulo="Sin consultas previas"
          color={C.indigo}
        />
        <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', py: 1 }}>
          El A/P de la última consulta aparecerá aquí cuando se registre
        </Typography>
      </Paper>
    );
  }

  const fecha = new Date(consulta.fecha_consulta);
  const dias = diasDesde(consulta.fecha_consulta);
  const tipo = consulta.tipo_consulta || 'primera_vez';
  const esSubsecuente = tipo === 'subsecuente';
  const evolGen = consulta.evolucion_problemas?.evolucion_general;
  const adherencia = consulta.evolucion_problemas?.adherencia_tratamiento;

  // Assessment: para subsecuentes priorizamos evolución; para primera vez la impresión diagnóstica
  const assessmentText = esSubsecuente && evolGen
    ? evolGen
    : consulta.impresion_diagnostica;
  const planText = consulta.plan_tratamiento;

  const tipoConfig = {
    primera_vez: { color: C.indigo, label: 'Primera vez' },
    subsecuente: { color: C.azul, label: 'Subsecuente' },
    urgencia: { color: C.rojo, label: 'Urgencia' },
    interconsulta: { color: C.morado, label: 'Interconsulta' },
  };
  const tipoCfg = tipoConfig[tipo] || tipoConfig.primera_vez;

  const pronosticoColor = {
    bueno: C.verde, reservado: C.ambar, malo: C.rojo,
  }[(consulta.pronostico || '').toLowerCase()] || null;

  // Parser simple para bullet points en plan
  const parsePlan = (txt) => {
    if (!txt) return [];
    const lines = txt.split(/\n|;|•|·/).map(l => l.trim()).filter(l => l.length > 2);
    return lines.length > 0 ? lines : [txt];
  };

  const planLines = parsePlan(planText);

  return (
    <Paper sx={{
      p: 2, borderRadius: 3,
      background: 'linear-gradient(180deg, #fafbff 0%, #ffffff 50%)',
      border: `1px solid ${tipoCfg.color.light}`,
      borderLeft: `4px solid ${tipoCfg.color.main}`,
      boxShadow: `0 2px 10px ${tipoCfg.color.light}55`,
    }}>
      {/* Header con fecha + tipo */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 1.5,
            background: tipoCfg.color.main,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 8px ${tipoCfg.color.light}`,
          }}>
            <AssignmentIcon sx={{ fontSize: 18, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{
              fontSize: 10, fontWeight: 800, color: tipoCfg.color.text,
              textTransform: 'uppercase', letterSpacing: 0.6, lineHeight: 1,
            }}>
              Último Assessment & Plan
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#475569', fontWeight: 600, mt: 0.2 }}>
              {fecha.toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })}
              <span style={{ color: '#94a3b8', fontWeight: 500 }}>
                {' · hace '}{dias === 0 ? 'hoy' : dias === 1 ? '1 día' : `${dias} días`}
              </span>
            </Typography>
          </Box>
        </Box>
        <Chip
          label={tipoCfg.label}
          size="small"
          sx={{
            background: tipoCfg.color.bg, color: tipoCfg.color.text,
            fontWeight: 700, fontSize: 10, height: 22,
            border: `1px solid ${tipoCfg.color.light}`,
          }}
        />
      </Box>

      {/* Motivo (si existe) */}
      {consulta.motivo_consulta && (
        <Box sx={{
          p: 1, mb: 1.2, borderRadius: 1.5,
          background: '#f8fafc', border: '1px solid #e2e8f0',
        }}>
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.2 }}>
            Motivo
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: '#334155', lineHeight: 1.4 }}>
            {truncar(consulta.motivo_consulta, 140)}
          </Typography>
        </Box>
      )}

      {/* Assessment (A) */}
      <Box sx={{ display: 'flex', gap: 1.2, mb: 1.2 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: 1, flexShrink: 0,
          background: tipoCfg.color.main, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 13,
        }}>
          A
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: tipoCfg.color.text, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.2 }}>
            Assessment {esSubsecuente && evolGen ? '· Evolución' : '· Impresión diagnóstica'}
          </Typography>
          {assessmentText ? (
            <Typography sx={{ fontSize: 13, color: '#1e293b', lineHeight: 1.45 }}>
              {truncar(assessmentText, 280)}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
              No registrado
            </Typography>
          )}
        </Box>
      </Box>

      {/* Plan (P) */}
      <Box sx={{ display: 'flex', gap: 1.2 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: 1, flexShrink: 0,
          background: C.morado.main, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 800, fontSize: 13,
        }}>
          P
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: C.morado.text, textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.2 }}>
            Plan de tratamiento
          </Typography>
          {planLines.length > 0 ? (
            planLines.length === 1 ? (
              <Typography sx={{ fontSize: 13, color: '#1e293b', lineHeight: 1.45 }}>
                {truncar(planLines[0], 280)}
              </Typography>
            ) : (
              <Box component="ul" sx={{ m: 0, pl: 2, listStyleType: 'disc' }}>
                {planLines.slice(0, 4).map((l, i) => (
                  <Typography key={i} component="li" sx={{ fontSize: 12.5, color: '#1e293b', lineHeight: 1.45, mb: 0.2 }}>
                    {truncar(l, 140)}
                  </Typography>
                ))}
                {planLines.length > 4 && (
                  <Typography sx={{ fontSize: 11, color: '#64748b', fontStyle: 'italic', mt: 0.3 }}>
                    + {planLines.length - 4} indicaciones más
                  </Typography>
                )}
              </Box>
            )
          ) : (
            <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
              No registrado
            </Typography>
          )}
        </Box>
      </Box>

      {/* Footer: pronóstico + adherencia */}
      {(pronosticoColor || adherencia || consulta.indicaciones_paciente) && (
        <Box sx={{
          mt: 1.2, pt: 1, borderTop: '1px dashed #e2e8f0',
          display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap',
        }}>
          {pronosticoColor && (
            <Chip
              label={`Pronóstico: ${consulta.pronostico}`}
              size="small"
              sx={{
                background: pronosticoColor.bg, color: pronosticoColor.text,
                fontWeight: 700, fontSize: 10, height: 22,
              }}
            />
          )}
          {adherencia && (
            <Chip
              label={`Adherencia: ${adherencia}`}
              size="small"
              sx={{
                background: C.azul.bg, color: C.azul.text,
                fontWeight: 700, fontSize: 10, height: 22,
              }}
            />
          )}
          {consulta.indicaciones_paciente && (
            <Tooltip title={truncar(consulta.indicaciones_paciente, 240)}>
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 12, color: `${C.verde.text} !important` }} />}
                label="Indicaciones al paciente"
                size="small"
                sx={{
                  background: C.verde.bg, color: C.verde.text,
                  fontWeight: 700, fontSize: 10, height: 22,
                  cursor: 'help',
                }}
              />
            </Tooltip>
          )}
        </Box>
      )}
    </Paper>
  );
}

function UltimosLabs({ consultas }) {
  // Extraer todos los parámetros de laboratorio de todas las consultas
  const parametrosPorNombre = useMemo(() => {
    const mapa = new Map();
    for (const c of consultas) {
      const fechaC = c.fecha_consulta;
      const estudios = c.evolucion_problemas?.estudios_revisados;
      if (!Array.isArray(estudios)) continue;
      for (const e of estudios) {
        const fechaE = e.fecha || fechaC;
        const params = e.parametros;
        if (!Array.isArray(params)) continue;
        for (const p of params) {
          if (!p?.nombre || p.valor === undefined || p.valor === null || p.valor === '') continue;
          const key = String(p.nombre).trim().toLowerCase();
          if (!key) continue;
          const valorNum = parseFloat(String(p.valor).replace(',', '.'));
          if (!mapa.has(key)) mapa.set(key, []);
          mapa.get(key).push({
            nombre: p.nombre,
            valor: p.valor,
            valorNum: Number.isFinite(valorNum) ? valorNum : null,
            unidad: p.unidad || '',
            referencia: p.referencia || '',
            estado: (p.estado || '').toLowerCase(),
            fecha: fechaE,
          });
        }
      }
    }
    // Ordenar cada grupo por fecha desc
    for (const [k, arr] of mapa) {
      arr.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    }
    return mapa;
  }, [consultas]);

  // Tomar los más recientes (top 8)
  const labs = useMemo(() => {
    const items = [];
    for (const [, arr] of parametrosPorNombre) {
      const latest = arr[0];
      const previo = arr[1] || null;
      let tendencia = null;
      if (previo && latest.valorNum !== null && previo.valorNum !== null) {
        const delta = latest.valorNum - previo.valorNum;
        const pct = previo.valorNum !== 0 ? (delta / previo.valorNum) * 100 : 0;
        if (Math.abs(pct) < 3) tendencia = 'flat';
        else if (delta > 0) tendencia = 'up';
        else tendencia = 'down';
      }
      items.push({ ...latest, previo, tendencia });
    }
    items.sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
    return items.slice(0, 8);
  }, [parametrosPorNombre]);

  if (labs.length === 0) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3, border: '1px dashed #cbd5e1', background: '#f8fafc' }}>
        <SeccionHeader
          icono={<BiotechIcon />}
          titulo="Últimos resultados"
          subtitulo="Sin laboratorios capturados"
          color={C.azul}
        />
        <Typography sx={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center', py: 1 }}>
          Los resultados aparecerán cuando se carguen en una nota de evolución
        </Typography>
      </Paper>
    );
  }

  const fechaMasReciente = labs[0]?.fecha;
  const dias = fechaMasReciente ? diasDesde(fechaMasReciente) : null;

  return (
    <Paper sx={{ p: 2, borderRadius: 3, border: '1px solid #e2e8f0' }}>
      <SeccionHeader
        icono={<BiotechIcon />}
        titulo="Últimos resultados de laboratorio"
        subtitulo={
          fechaMasReciente
            ? `Más reciente · hace ${dias === 0 ? 'hoy' : dias === 1 ? '1 día' : `${dias} días`}`
            : ''
        }
        color={C.azul}
      />
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 1,
      }}>
        {labs.map((lab, i) => (
          <LabCard key={i} lab={lab} />
        ))}
      </Box>
    </Paper>
  );
}

function LabCard({ lab }) {
  const estadoConfig = {
    alto:   { color: C.rojo,  label: 'ALTO',   simbolo: '↑' },
    bajo:   { color: C.azul,  label: 'BAJO',   simbolo: '↓' },
    normal: { color: C.verde, label: 'NORMAL', simbolo: '✓' },
  };
  const cfg = estadoConfig[lab.estado] || { color: C.slate, label: '', simbolo: '' };

  const tendIcono = {
    up:   { icono: <TrendingUpIcon sx={{ fontSize: 14 }} />,   color: '#f97316', tooltip: 'Subió respecto al anterior' },
    down: { icono: <TrendingDownIcon sx={{ fontSize: 14 }} />, color: '#0284c7', tooltip: 'Bajó respecto al anterior' },
    flat: { icono: <TrendingFlatIcon sx={{ fontSize: 14 }} />, color: '#94a3b8', tooltip: 'Sin cambios' },
  }[lab.tendencia];

  const fechaCorta = new Date(lab.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' });
  const tooltipDetalle = lab.previo
    ? `Previo: ${lab.previo.valor} ${lab.previo.unidad || ''} (${new Date(lab.previo.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric' })})`
    : 'Sin valor anterior para comparar';

  return (
    <Tooltip
      title={
        <Box sx={{ p: 0.3 }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, mb: 0.4 }}>{lab.nombre}</Typography>
          {lab.referencia && (
            <Typography sx={{ fontSize: 10, opacity: 0.85 }}>Ref: {lab.referencia} {lab.unidad}</Typography>
          )}
          <Typography sx={{ fontSize: 10, opacity: 0.85, mt: 0.3 }}>{tooltipDetalle}</Typography>
        </Box>
      }
      arrow
    >
      <Paper
        elevation={0}
        sx={{
          p: 1, borderRadius: 2,
          background: cfg.color.bg,
          border: `1px solid ${cfg.color.light}`,
          borderLeft: `3px solid ${cfg.color.main}`,
          cursor: 'help',
          transition: 'all 0.18s',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 4px 12px ${cfg.color.light}`,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.3 }}>
          <Typography sx={{
            fontSize: 9.5, fontWeight: 800, color: cfg.color.text,
            textTransform: 'uppercase', letterSpacing: 0.4,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            maxWidth: '85%',
          }}>
            {lab.nombre}
          </Typography>
          {tendIcono && (
            <Box sx={{ color: tendIcono.color, display: 'flex', alignItems: 'center' }}>
              {tendIcono.icono}
            </Box>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: cfg.color.text, lineHeight: 1.2 }}>
            {lab.valor}
          </Typography>
          {lab.unidad && (
            <Typography sx={{ fontSize: 10.5, color: cfg.color.text, opacity: 0.85, fontWeight: 600 }}>
              {lab.unidad}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.4 }}>
          {cfg.label && (
            <Typography sx={{
              fontSize: 9.5, fontWeight: 800, color: cfg.color.main,
              letterSpacing: 0.4,
            }}>
              {cfg.simbolo} {cfg.label}
            </Typography>
          )}
          <Typography sx={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>
            {fechaCorta}
          </Typography>
        </Box>
      </Paper>
    </Tooltip>
  );
}

function TimelineHorizontal({ consultas }) {
  return (
    <Box sx={{ position: 'relative', pt: 1, pb: 0.5 }}>
      {/* Línea horizontal de fondo */}
      <Box sx={{
        position: 'absolute',
        left: 18, right: 18, top: 24,
        height: 2,
        background: 'linear-gradient(90deg, #c7d2fe 0%, #e2e8f0 100%)',
      }} />

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${consultas.length}, 1fr)`,
        gap: 1, position: 'relative',
      }}>
        {consultas.map((c, i) => {
          const fecha = new Date(c.fecha_consulta);
          const dias = diasDesde(c.fecha_consulta);
          const tipo = c.tipo_consulta;
          const colorTipo = tipo === 'subsecuente' ? C.azul
                           : tipo === 'urgencia' ? C.rojo
                           : tipo === 'interconsulta' ? C.morado
                           : C.indigo;
          return (
            <Box key={c.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative' }}>
              {/* Punto del timeline */}
              <Box sx={{
                width: 14, height: 14, borderRadius: '50%',
                background: colorTipo.main,
                border: '3px solid #fff',
                boxShadow: `0 0 0 1.5px ${colorTipo.main}`,
                zIndex: 1, mb: 0.5,
              }} />
              {/* Card de la consulta */}
              <Paper sx={{
                p: 0.7, borderRadius: 1.5, border: `1px solid ${colorTipo.light}`,
                background: '#fff', width: '100%', minWidth: 0,
                textAlign: 'center',
              }}>
                <Typography sx={{
                  fontSize: 9, fontWeight: 800, color: colorTipo.text,
                  textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.2,
                }}>
                  {fecha.toLocaleDateString('es', { day: '2-digit', month: 'short' })}
                </Typography>
                <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>
                  {dias === 0 ? 'hoy' : dias === 1 ? 'ayer' : `hace ${dias}d`}
                </Typography>
                <Typography sx={{
                  fontSize: 10, color: '#334155', fontWeight: 600,
                  mt: 0.3, lineHeight: 1.3,
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}>
                  {c.motivo_consulta || '(sin motivo)'}
                </Typography>
              </Paper>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

function ProximasAcciones({ proximasCitas, estudiosPendientes }) {
  const totalAcciones = proximasCitas.length + estudiosPendientes.length;

  if (totalAcciones === 0) {
    return (
      <Box sx={{ py: 2.5, textAlign: 'center' }}>
        <CheckCircleIcon sx={{ fontSize: 36, color: C.verde.light, mb: 0.5 }} />
        <Typography sx={{ fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>
          Sin acciones pendientes
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.7 }}>
      {proximasCitas.slice(0, 3).map((cita, i) => {
        const fecha = new Date(cita.fecha_hora);
        const dias = Math.ceil((fecha.getTime() - Date.now()) / (24 * 3600 * 1000));
        return (
          <Box key={`cita-${i}`} sx={{
            display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5,
            background: '#eff6ff', border: '1px solid #bfdbfe',
            borderLeft: '3px solid #3b82f6',
          }}>
            <EventIcon sx={{ fontSize: 18, color: '#2563eb' }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#1e3a8a' }}>
                Cita: {cita.motivo || cita.tipo || 'Consulta'}
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#1e40af' }}>
                {fecha.toLocaleString('es', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                {' · '}
                {dias === 0 ? 'hoy' : dias === 1 ? 'mañana' : `en ${dias} días`}
              </Typography>
            </Box>
            <ArrowForwardIcon sx={{ fontSize: 16, color: '#2563eb' }} />
          </Box>
        );
      })}
      {estudiosPendientes.slice(0, 4).map((est, i) => (
        <Box key={`est-${i}`} sx={{
          display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5,
          background: '#fffbeb', border: '1px solid #fde68a',
          borderLeft: '3px solid #f59e0b',
        }}>
          <ScienceIcon sx={{ fontSize: 18, color: '#d97706' }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#78350f' }}>
              {est.nombre_estudio || 'Estudio pendiente'}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#92400e' }}>
              Solicitado: {new Date(est.fecha_solicitud).toLocaleDateString('es', { day: '2-digit', month: 'short' })}
              {' · '}
              <span style={{ fontWeight: 700 }}>resultado pendiente</span>
            </Typography>
          </Box>
        </Box>
      ))}
      {(proximasCitas.length > 3 || estudiosPendientes.length > 4) && (
        <Typography sx={{ fontSize: 10.5, color: '#64748b', fontStyle: 'italic', textAlign: 'center', mt: 0.3 }}>
          + {(proximasCitas.length - 3) + (estudiosPendientes.length - 4)} más
        </Typography>
      )}
    </Box>
  );
}

// ============================================================================
// HELPERS
// ============================================================================

function calcEdad(fechaISO) {
  if (!fechaISO) return '—';
  const dob = new Date(fechaISO);
  return Math.floor((Date.now() - dob.getTime()) / (365.25 * 24 * 3600 * 1000));
}

function diasDesde(iso) {
  if (!iso) return '—';
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 3600 * 1000));
  return dias;
}

function truncar(txt, max = 200) {
  if (!txt) return '';
  const s = String(txt).trim();
  return s.length > max ? s.slice(0, max - 1).trim() + '…' : s;
}

function extraerMedicamentosActivos(recetas) {
  if (!recetas?.length) return [];
  const hace90dias = Date.now() - 90 * 24 * 3600 * 1000;
  const recientes = recetas.filter(r => new Date(r.fecha_emision).getTime() >= hace90dias);
  const meds = [];
  for (const r of recientes) {
    const ms = r.medicamentos || [];
    if (Array.isArray(ms)) {
      for (const m of ms) {
        meds.push(typeof m === 'object' ? m : { nombre: String(m) });
      }
    }
  }
  const vistos = new Set();
  return meds.filter(m => {
    const k = (m.nombre || '').toLowerCase();
    if (!k || vistos.has(k)) return false;
    vistos.add(k);
    return true;
  });
}

function extraerHeredoFamiliares(expediente) {
  if (!expediente) return [];
  const a = expediente.antecedentes_heredo_familiares || {};
  const items = [];
  for (const [k, v] of Object.entries(a)) {
    if (!v) continue;
    if (typeof v === 'string' && v.trim()) {
      items.push(`${k.replace(/_/g, ' ')}: ${v}`);
    } else if (Array.isArray(v) && v.length > 0) {
      items.push(`${k.replace(/_/g, ' ')} (${v.length})`);
    } else if (typeof v === 'boolean' && v) {
      items.push(k.replace(/_/g, ' '));
    }
  }
  return items.slice(0, 8);
}

function extraerNoPatologicos(expediente) {
  if (!expediente) return [];
  const a = expediente.antecedentes_personales_no_patologicos || {};
  const items = [];
  // Hábitos típicos
  if (a.tabaquismo === true || a.tabaquismo === 'si' || a.fuma) items.push('🚬 Tabaquismo');
  if (a.alcoholismo === true || a.alcoholismo === 'si' || a.alcohol) items.push('🍷 Alcohol');
  if (a.drogas === true || a.drogas === 'si') items.push('💊 Uso de drogas');
  if (a.actividad_fisica === false || a.sedentarismo) items.push('🪑 Sedentarismo');
  if (a.actividad_fisica === true || a.ejercicio) items.push('🏃 Ejercicio regular');
  if (a.dieta_balanceada === false) items.push('🍔 Dieta no balanceada');
  if (a.dieta_balanceada === true) items.push('🥗 Dieta balanceada');
  // Texto libre
  if (typeof a === 'string' && a.trim()) items.push(a.slice(0, 80));
  if (a.observaciones) items.push(a.observaciones.slice(0, 80));
  return items.slice(0, 6);
}

function construirBriefing(paciente, consultas, tendencia, problemas) {
  if (!paciente) return 'Cargando información del paciente...';

  if (consultas.length === 0) {
    const texto = problemas.length > 0
      ? `${paciente.nombre_completo?.split(' ')[0] || 'Paciente'}, ${calcEdad(paciente.fecha_nacimiento)} años. Con ${problemas.length} problema${problemas.length > 1 ? 's' : ''} crónico${problemas.length > 1 ? 's' : ''} registrado${problemas.length > 1 ? 's' : ''}. Aún sin consultas en el sistema — inicia la primera para construir su historia clínica.`
      : `${paciente.nombre_completo?.split(' ')[0] || 'Paciente'}, ${calcEdad(paciente.fecha_nacimiento)} años. Paciente nuevo sin historial. Inicia la primera consulta para comenzar su expediente.`;
    return texto;
  }

  const ultima = consultas[0];
  const totalConsultas = consultas.length;
  const dias = diasDesde(ultima.fecha_consulta);
  const fraseTiempo = dias === 0 ? 'hoy' : dias === 1 ? 'ayer' : `hace ${dias} días`;

  let frase = `${paciente.nombre_completo?.split(' ').slice(0, 2).join(' ') || 'Paciente'}, ${calcEdad(paciente.fecha_nacimiento)} años. `;
  frase += `${totalConsultas} consulta${totalConsultas > 1 ? 's' : ''} registrada${totalConsultas > 1 ? 's' : ''}, última ${fraseTiempo}`;

  if (ultima.motivo_consulta) {
    frase += ` por "${ultima.motivo_consulta.slice(0, 60)}${ultima.motivo_consulta.length > 60 ? '...' : ''}"`;
  }
  frase += '. ';

  if (tendencia.TA === 'up') {
    frase += 'Se observa tendencia al alza en tensión arterial — considerar reevaluación.';
  } else if (tendencia.TA === 'down') {
    frase += 'TA con tendencia favorable a la baja.';
  } else if (problemas.length >= 3) {
    frase += `Paciente con ${problemas.length} comorbilidades activas — requiere seguimiento estrecho.`;
  } else if (dias > 180) {
    frase += 'Sin control reciente — considerar agendar cita de seguimiento.';
  }

  return frase;
}

function calcularTendencia(datos) {
  const t = {};
  if (datos.length < 3) return t;
  const ultimos = datos.slice(-3);
  const sysStart = ultimos[0].TA_sys;
  const sysEnd = ultimos[ultimos.length - 1].TA_sys;
  if (sysStart != null && sysEnd != null) {
    const diff = sysEnd - sysStart;
    t.TA = diff > 5 ? 'up' : diff < -5 ? 'down' : 'flat';
  }
  const fcStart = ultimos[0].FC;
  const fcEnd = ultimos[ultimos.length - 1].FC;
  if (fcStart != null && fcEnd != null) {
    const diff = fcEnd - fcStart;
    t.FC = diff > 5 ? 'up' : diff < -5 ? 'down' : 'flat';
  }
  return t;
}
